import React from 'react';
import ModalImage from "react-modal-image";
import MonitorTypes from "./monitorTypes.js";

const Overview = () => {
  return(
    <article id="overview">
      <h1 className="space-left bold">Project Overview</h1>
      <section>
        <h2>SmartCrib<span role="img" aria-label="baby emoji"> 👶</span></h2>
        <article className="flex">
          <figure>
            <img id="logo" src="./img/SmartCrib.png" alt="SmartCrib Logo: a colorful crib with a mobile that hangs the letters 'S', 'M', 'A', 'R', 'T'" />
            <figcaption>
              SmartCrib Logo
              <p className="small-text">Designed by Stella Lau</p>
            </figcaption>
          </figure>
          <p>
            According to the Centers for Disease Control and Prevention (CDC), around
            <strong> 3,600</strong> babies in the United States suffer from sudden unexpected infant
            death (SUID) each year. Most cases of SUID occurred when the babies were asleep, and
            parents unaware of the incidents
            <cite>
              <a href="https://www.cdc.gov/sids/about/index.htm?CDC_AA_refVal=https%3A%2F%2Fwww.cdc.gov%2Fsids%2FAboutSUIDandSIDS.htm" target="_blank" rel="noreferrer"> [1] </a>
            </cite>.
            Although home infant monitors have not been found to prevent SUID in a general population
            <cite>
              <a href="https://pediatrics.aappublications.org/content/111/4/914" target="_blank" rel="noreferrer"> [2] </a>
            </cite>
            <cite>
              <a href="https://jamanetwork.com/journals/jama/article-abstract/193788" target="_blank" rel="noreferrer"> [3]</a>
            </cite>
            , they can provide peace of mind for concerned parents
            <cite>
              <a href="https://www.bmj.com/content/349/bmj.g6639" target="_blank" rel="noreferrer"> [4] </a>
            </cite>
            and may be clinically relevant for at-home preterm or high-risk neonatal care
            <cite>
              <a href="https://www.healio.com/pediatrics/journals/pedann/2017-8-46-8/%7Bbbb919ba-9aa5-45b3-849d-1bad1bbca090%7D/the-history-of-home-cardiorespiratory-monitoring" target="_blank" rel="noreferrer"> [5]</a>
            </cite>.
            To serve the market of parents of infants, we propose SmartCrib, a comprehensive baby
            monitoring system intended for all parents.
          </p>
          <p>
            SmartCrib is our reimagination of baby monitors as engineering students and some of us
            parents. It is a more comprehensive, informative, accessible, and secure approach to
            baby monitoring than current baby monitors on the market. It uses digital and analog
            sensors to collect health metrics from babies, including their pulse, breathing,
            movement, and noise. When SmartCrib detects any irregular data, it sends warning
            messages to the parents, which allows parents to rest well, run errands, or do chores
            in the house without worrying about their babies all the time. It also captures ambient
            environmental data, including temperature, humidity, and light level. It then displays
            the current data on an online dashboard, which enables parents with visual impairments
            to access them with screen readers. Parents can also observe trends of different data
            on the dashboard, so they can accurately report the data to their pediatricians or
            adjust ambient environmental factors to improve their infants’ sleep. The SmartCrib
            neither records audio or video footage nor allows any communication, which prevents
            eavesdropping, peeping, harassment from hackers. We tested the prototype ourselves;
            no baby is involved in testing. Calibration will be necessary if applying the same
            prototype and techniques on babies. See Project Components for more details.
          </p>
        </article>
      </section>
      <section>
        <h2>Current Baby Monitors on the Market</h2>
        <p>
          There are currently 3 major types of baby monitors on the market: audio-only, not Wi-Fi
          compatible video-and-audio, and Wi-Fi compatible video-and-audio. We collected 20 samples
          of the most popular and highly-rate (all over 4-star ratings with more than 1000 reviews
          on <time dateTime="2020-11-29">11/29/2020</time>) baby monitors on Amazon. Two out of
          twenty are audio-only, while the majority are video-and-audio. The average cost of
          audio-only baby monitors in the samples collected sits at $29.49. Seven out of twenty are
          not Wi-Fi compatible video-and-audio with an average price of $94.41. The majority
          (11 out of 20) are Wi-Fi compatible video-and-audio, with an average price of around $64.81.
          An example of a similar product,
          <a href="https://www.amazon.com/Owlet-Baby-Monitor-Infants-Ultimate/dp/B06ZZXYD6S/ref=sr_1_4?crid=2C1I57KZF2I77&dchild=1&keywords=owlet+smart+sock&qid=1606865383&sprefix=owlet+%2Caps%2C221&sr=8-4" target="_blank" rel="noreferrer"> Owlet Smart Sock 2</a>,
          only measures the heart rate and oxygen level cost over $200 on Amazon on
          <time dateTime="2020-12-15"> 12/15/2020</time>.
        </p>
        <MonitorTypes />
        <p>
          The audio-only baby monitors work pretty much like one-way or two-way walkie-talkies.
          With the one-way audio monitors, parents can only listen to real-time audio footage
          recorded from the nursery, while the two-way audio monitors allow parents to communicate
          with their babies as well. Even though these monitors don't require Wi-Fi connections,
          they are subject to data breaches and harassment from hackers within the transmission
          range. Some of them use digital signals to improve security. However, there are no
          encryption standards set for baby monitors yet.
        </p>
        <p>
          The video & audio baby monitors can be either Wi-Fi compatible or not. The ones that are
          not Wi-Fi compatible work pretty much like two-way walkie-talkies, except you also get
          real-time video footage of the nursery. The fact they provide video footage makes them
          vulnerable to not only eavesdropping but also peeping. Although Wi-Fi compatible monitors
          are more convenient, and the service is not limited to a certain range. It makes them
          even more vulnerable because hackers also don't have to be close by to gain control over
          the monitors.
        </p>
      </section>
      <section>
        <h2>SmartCrib VS Current Baby Monitors on the Market</h2>
        <div id="compare" className="flex">
          <article>
            <h3>
              <span role="img" aria-label="heart emoji"> ❣️ </span>SmartCrib<span role="img" aria-label="heart emoji"> ❣️ </span>
            </h3>
            <p>Wi-Fi Compatible</p>
            <p>
              Estimate Wholesale Price: $100-120
              <br />
              (<a href="https://docs.google.com/spreadsheets/d/1hS2DZ1XewIjzBUSle_P7GrGKtgPbmIN6CgUBECKdcL8/edit?usp=sharing" target="_blank" rel="noreferrer">Cost Breakdown</a>)
            </p>
            <p>Health Metrics & Ambient Environmental Data</p>
            <p>Mobile/Web Interface</p>
          </article>
          <article>
            <h3>Audio-only</h3>
            <p>Not Wi-Fi Compatible (mostly)</p>
            <p>
              $15-80
              (
              <cite>
                <a href="https://www.babymonitorsguide.net/how-much-do-baby-monitor-cost/#:~:text=The%20typical%20cost%20of%20a,additional%20features%20they%20are%20offering." target="_blank" rel="noreferrer">Source</a>
              </cite>
              )
            </p>
            <p>Audio Transmission and Ambient Environmental Data (Some)</p>
            <p>No Mobile/Web Interface</p>
          </article>
          <article>
            <h3>Video & Audio</h3>
            <p>Wi-Fi Compatible / Not Wi-Fi Compatible</p>
            <p>
              $40-900
              (
              <cite>
                <a href="https://www.babymonitorsguide.net/how-much-do-baby-monitor-cost/#:~:text=The%20typical%20cost%20of%20a,additional%20features%20they%20are%20offering." target="_blank" rel="noreferrer">Source</a>
              </cite>
              )
            </p>
            <p>Video Footage, Audio Transmission, and Ambient Environmental Data (some)</p>
            <p>Mobile/Web Interface (Wi-Fi Compatible Ones)</p>
          </article>
        </div>
      </section>
      <section id="concept">
        <h2>Concept Design</h2>
        <article>
          <figure>
            <ModalImage
              small="./img/project/concept.png"
              large="./img/project/concept.png"
              hideZoom={true}
              hideDownload={true}
              showRotate={false}
              alt="Conceptual overview diagram of SmartCrib"
            />
            <figcaption className="small-text">
              Designed by Michael Fiscus
            </figcaption>
          </figure>
          <p>
            We envisioned a basic product design of SmartCrib based on our prototype. It will be a
            baby monitoring system that can be attached to any crib. The sound and temperature &
            humidity sensors will be embedded into the SmartCrib Hub, which will also hold the
            microcontroller. SmartCrib will also come with a sensor strap that will be connected to
            the pulse sensor. The respiratory rate will be collected with a sensor clip clipped onto
            the diaper that will host an accelerometer-gyroscope. The motion will be measured with
            an infrared motion sensor. All the sensors will be wirelessly connected to the
            microcontroller and all the data will be displayed on a local OLED display.
          </p>
        </article>
        <article>
          <figure>
            <ModalImage
              small="./img/project/sensor.png"
              large="./img/project/sensor.png"
              hideDownload={true}
              hideZoom={true}
              showRotate={false}
              alt="Conceptual diagram that demonstrates how the pulse-oximeter will be attached"
            />
            <figcaption className="small-text">
              Designed by Michael Fiscus
            </figcaption>
          </figure>
          <figure>
            <ModalImage
              small="./img/project/dorsalis.jpg"
              large="./img/project/dorsalis.jpg"
              hideDownload={true}
              hideZoom={true}
              showRotate={false}
              alt="Foot diagram that shows the location of the dorsalis pedis artery"
            />
            <figcaption className="small-text">
              <cite>
                <a href="https://www.stepwards.com/?page_id=4020" target="_blank" rel="noreferrer">Stepwards.com</a>
              </cite>
            </figcaption>
          </figure>
          <p>
            A pulse-oximeter will be attached to an adjustable fabric strap to allow contact between
            the top of the foot and the sensor to measure the heart rate from the dorsalis artery.
            An RF transmitter and a thin-profile battery will allow wireless data transmission, with
            hours of usage time. This will all be housed within a padded plastic frame, which will
            include an interface to monitor device status through LEDs, power the device, and charge
            it.
          </p>
          <figure>
            <ModalImage
              small="./img/project/respiratory.png"
              large="./img/project/respiratory.png"
              hideDownload={true}
              hideZoom={true}
              showRotate={false}
              alt="Conceptual diagram that demonstrates how the accelerometer-gyroscope will be attached"
            />
            <figcaption className="small-text">
              Designed by Michael Fiscus
            </figcaption>
          </figure>
          <p>
            A 3-axis accelerometer-gyroscope will be used to detect abdomen movement to measure the
            breathing rate since
            <cite>
              <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6339050/" target="_blank" rel="noreferrer"> research </a>
            </cite>
            has proven this approach to measure respiratory rate to be accurate. Choosing the
            abdomen as the connection point will also allow usage on infants dressed solely in a
            diaper (clothing that covers the torso will not be required for device operation). The
            diaper clip on the backside of the device housing will help attach the device to the
            infant. Like the sensor strap, an RF transmitter and thin-profile battery will also be
            used to transmit data wirelessly over a long period of time. The housing of the device
            will use the same fabric material as the sensor strap for consumer comfortability. It
            will also include an exterior silicone protection ring to provide comfort to the infant
            sand device protection against drops.
          </p>
        </article>
      </section>
      <p id="to-top">
        <a href="#overview"> Back to Top </a>
      </p>
    </article>
  );
}

export default Overview;
